import React from "react";
import { min } from "mathjs";

import { useAppSelector } from "store";
import { selectStore } from "modules/customer/insights/portfolioNew/portfolioSlice";
import {
    selectLevelOfCannibalisation,
    selectSelectedCannibalisedStores
} from "modules/customer/insights/portfolioNew/competition/competitionSlice";
import { selectMapboxConfiguration } from "modules/siteConfiguration/siteConfigurationSlice";

import { Box } from "@material-ui/core";

import useColourPalette from "components/visuals/useColourPalette";
import OutputAreasMap, { pointerInfoEmpty } from "components/visuals/maps/OutputAreasMap";
import SelectedStoreMarker from "components/visuals/maps/SelectedStoreMarker";

import ExistingStore from "./ExistingStore";
import Legend from "./Legend";

const Map: React.FC = () => {
    const colourPalette = useColourPalette();
    const mapboxConfiguration = useAppSelector(selectMapboxConfiguration);
    const selectedStore = useAppSelector(selectStore);
    const { isLoading, hasErrors, data: cannibalisationOutputAreas } = useAppSelector(selectLevelOfCannibalisation);
    const existingStores = useAppSelector(selectSelectedCannibalisedStores);
    const [hoverInfo, setHoverInfo] = React.useState(pointerInfoEmpty);
    const hoveredOutputArea = cannibalisationOutputAreas.find(oa => oa.code === hoverInfo.outputAreaCode);
    const [initialViewport, setInitialViewport] = React.useState({
        latitude: selectedStore?.latitude ?? 0,
        longitude: selectedStore?.longitude ?? 0,
        zoom: 13.5,
        minZoom: 4
    });

    const downloadData = cannibalisationOutputAreas.map(({ catchmentStoreIDs, ...item }) => item);

    const mapOutputAreas = React.useMemo(() => {
        return cannibalisationOutputAreas.map(oa => ({
            code: oa.code,
            colourGroup: oa.type === "Existing" ? 0 : min(1 + oa.levelOfCannibalisation, 5)
        }));
    }, [cannibalisationOutputAreas]);

    React.useEffect(() => {
        setInitialViewport(current => {
            return {
                ...current,
                latitude: selectedStore?.latitude ?? 0,
                longitude: selectedStore?.longitude ?? 0
            };
        });
    }, [selectedStore]);

    return (
        <Box data-cy="cannibalisation-map">
            <OutputAreasMap
                loading={isLoading}
                error={hasErrors}
                title={`${selectedStore?.name} store catchment area cannibalisation with 5 closest stores`}
                mapboxAccessToken={mapboxConfiguration.accessToken}
                mapboxBaseMapStyle={mapboxConfiguration.baseMapStyle}
                mapboxOutputAreasTilesetId={mapboxConfiguration.outputAreasTilesetId}
                mapboxOutputAreasTilesetUrl={mapboxConfiguration.outputAreasTilesetUrl}
                initialViewport={initialViewport}
                addGeocoder={false}
                outputAreas={mapOutputAreas}
                colours={colourPalette.cannibalisation}
                onHover={setHoverInfo}
                enableHover={true}
                enableClick={false}
                displayPOIs={true}
                downloadData={downloadData}
            >
                <SelectedStoreMarker latitude={selectedStore?.latitude ?? 0} longitude={selectedStore?.longitude ?? 0} />
                {existingStores?.map((store, index) =>
                    <ExistingStore
                        key={index}
                        latitude={store.latitude}
                        longitude={store.longitude}
                    />
                )}
                <Legend outputArea={hoveredOutputArea} />
            </OutputAreasMap>
        </Box>
    );
};

export default Map;
